import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { createFeedback, subscribeFeedbacksByUser, updateFeedbackName, updateFeedback, type FeedbackDoc } from '@/dao/firestore/FeedbacksRepository';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
export interface FeedbackListProps {
  uid: string | null;
  onSelect: (fb: FeedbackDoc) => void;
}
export default function FeedbackList({
  uid,
  onSelect
}: FeedbackListProps) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FeedbackDoc[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const { toast } = useToast();
  const { t } = useTranslation();
  // Subscribe to feedbacks in real-time
  useEffect(() => {
    setLoading(true);
    if (!uid) {
      setItems([]);
      setSelectedId(null);
      setLoading(false);
      return;
    }
    let initial = true;
    let createdDuringInit = false;
    const unsub = subscribeFeedbacksByUser(uid, async (raw) => {
      if (initial && raw.length === 0 && !createdDuringInit) {
        createdDuringInit = true;
        const created = await createFeedback({ userId: uid });
        setItems([created]);
        setSelectedId(created.id);
        onSelect(created);
        initial = false;
        setLoading(false);
        return;
      }
      setItems(raw);
      
      // Only auto-select if no current selection exists or current selection is no longer valid
      if (!selectedId || !raw.find(i => i.id === selectedId)) {
        // For auto-selection, use only non-archived items if includeArchived is false
        const candidate = includeArchived ? raw : raw.filter(i => !i.archived);
        if (candidate.length > 0) {
          setSelectedId(candidate[0].id);
          onSelect(candidate[0]);
        }
      } else if (selectedId) {
        // Preserve current selection and update the selected item data
        const currentSelected = raw.find(i => i.id === selectedId);
        if (currentSelected) {
          onSelect(currentSelected);
        }
      }
      if (initial) {
        initial = false;
        setLoading(false);
      }
    });
    return () => unsub();
  }, [uid, selectedId, onSelect, includeArchived]);

  // Ensure selection remains valid when includeArchived changes
  useEffect(() => {
    if (!selectedId) return;
    
    const candidate = includeArchived ? items : items.filter(i => !i.archived);
    const currentSelected = items.find(i => i.id === selectedId);
    
    // Only reset selection if:
    // 1. Current selection is archived AND we're not showing archived items
    // 2. There are other items available to select
    if (currentSelected?.archived && !includeArchived && candidate.length > 0) {
      setSelectedId(candidate[0].id);
      onSelect(candidate[0]);
    }
  }, [includeArchived, items, selectedId, onSelect]);
  const filtered = useMemo(() => {
    const base = includeArchived ? items : items.filter(i => !i.archived);
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(i => i.name?.toLowerCase().includes(q));
  }, [items, search, includeArchived]);
  const handleCreate = async () => {
    if (!uid) return;
    const created = await createFeedback({
      userId: uid
    });
    // Don't manually add to items, subscribeFeedbacksByUser will handle it
    setSelectedId(created.id);
    onSelect(created);
  };
  const startEdit = (item: FeedbackDoc) => {
    if (item.archived) {
      toast({ title: t('studio.feedbackList.archivedFeedback'), description: t('studio.feedbackList.cannotEditArchived') });
      return;
    }
    setEditingId(item.id);
    setEditingName(item.name ?? '');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };
  const commitEdit = async () => {
    if (!editingId) return;
    const current = items.find(it => it.id === editingId);
    if (current?.archived) {
      cancelEdit();
      return;
    }
    const newName = editingName.trim() || new Date().toLocaleString();
    await updateFeedbackName(editingId, newName);
    setItems(prev => prev.map(it => it.id === editingId ? {
      ...it,
      name: newName
    } : it));
    if (selectedId === editingId) {
      const selected = items.find(i => i.id === editingId);
      if (selected) onSelect({
        ...selected,
        name: newName
      });
    }
    cancelEdit();
  };
  const duplicateItem = async (item: FeedbackDoc) => {
    if (!uid) return;
    try {
      const baseName = item.name || new Date().toLocaleString();
      
      // Create feedback with all fields copied (except id, status, dates, and name)
      const created = await createFeedback({
        userId: uid,
        name: `${baseName} ${t('studio.feedbackList.copy')}`,
        video: item.video,
        status: 'draft',
        archived: false,
        ageRange: item.ageRange,
        regions: item.regions,
        interests: item.interests,
        selectedPersonas: item.selectedPersonas,
        notes: item.notes,
        secondEditorOpinion: item.secondEditorOpinion,
      } as any);

      setSelectedId(created.id);
      onSelect(created);
    } catch (e) {
      console.error('Duplicate feedback error:', e);
    }
  };
    const archiveItem = async (item: FeedbackDoc) => {
    const archiving = !item.archived;

    let nextToSelect: FeedbackDoc | null = null;
    if (archiving && selectedId === item.id) {
      const idx = filtered.findIndex(i => i.id === item.id);
      // Preferir el anterior no archivado
      for (let i = idx - 1; i >= 0; i--) {
        const it = filtered[i];
        if (it && !it.archived) { nextToSelect = it; break; }
      }
      // Si no hay anterior, intentar el posterior no archivado
      if (!nextToSelect) {
        for (let i = idx + 1; i < filtered.length; i++) {
          const it = filtered[i];
          if (it && !it.archived) { nextToSelect = it; break; }
        }
      }
    }

    await updateFeedback(item.id, { archived: archiving });

    if (archiving && selectedId === item.id) {
      if (nextToSelect) {
        setSelectedId(nextToSelect.id);
        onSelect(nextToSelect);
      } else if (uid) {
        const created = await createFeedback({ userId: uid });
        // Don't manually add to items, subscribeFeedbacksByUser will handle it
        setSelectedId(created.id);
        onSelect(created);
      }
    }

  };
  const selectItem = (item: FeedbackDoc) => {
    if (item.archived) {
      toast({ title: t('studio.feedbackList.archivedFeedback'), description: t('studio.feedbackList.cannotSelectArchived') });
      return;
    }
    setSelectedId(item.id);
    onSelect(item);
  };
  return <div className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-2 my-[15px] mx-0">
        <Input placeholder={t('studio.feedbackList.search')} value={search} onChange={e => setSearch(e.target.value)} className="rounded-none my-[10px]" />
      </div>
      <div className="flex items-center justify-between py-0">
        <div className="text-xs text-muted-foreground">{t('studio.feedbackList.screeningRooms')}</div>
        <Button size="sm" variant="ghost" onClick={handleCreate} aria-label={t('studio.feedbackList.newFeedback')}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="space-y-2 pr-2">
          {loading && (
            <div className="p-2">
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-12 rounded-md" />
                ))}
              </div>
            </div>
          )}
          {!loading && filtered.length === 0 && <div className="text-sm text-muted-foreground">{t('studio.feedbackList.noData')}</div>}
          {filtered.map(item => {
          const isActive = selectedId === item.id;
          const isEditing = editingId === item.id;
              return <div key={item.id} className={`block group rounded-md border px-3 py-2 transition cursor-pointer  ${isActive ? 'border-primary' : 'hover:bg-muted'}`} onClick={() => selectItem(item)}>
                <div className="flex items-center ">
                  <div className="min-w-5 flex-1 overflow-hidden ">
                    {isEditing ? <Input 
                      value={editingName} 
                      onChange={e => setEditingName(e.target.value)} 
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }} 
                      onBlur={commitEdit} 
                      autoFocus 
                      onClick={e => e.stopPropagation()} 
                      className="min-w-0"
                    /> : <div className="font-medium truncate text-ellipsis overflow-hidden whitespace-nowrap" onDoubleClick={e => {
                      e.stopPropagation();
                      startEdit(item);
                    }} title={item.name}>{item.name}</div>}
                  </div>
                  <div className="float-end items-center gap-1 min-w-fit">
                    <Badge variant={item.status === 'running' ? 'warning' : item.status === 'done' ? 'success' : item.status === 'error' ? 'destructive' : 'secondary'}>{item.status}</Badge>
                    {item.archived && <Badge variant="outline">{t('studio.feedbackList.archived')}</Badge>}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity flex-shrink-0" onClick={e => e.stopPropagation()} aria-label={t('studio.feedbackList.openActions')}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" side="bottom" sideOffset={6} className="z-[60] bg-popover">
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); duplicateItem(item); }}>
                          {t('feedbacks.duplicate')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={e => {
                          e.stopPropagation();
                          archiveItem(item);
                        }}>
                          {item.archived ? t('studio.feedbackList.unarchive') : t('studio.feedbackList.archive')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </ScrollArea>
      <div className="pt-2 mt-2 border-t">
        <label htmlFor="archived" className="flex items-center gap-2 text-sm text-muted-foreground py-[15px] px-0 mx-0">
          <Checkbox id="archived" checked={includeArchived} onCheckedChange={(v: any) => setIncludeArchived(!!v)} />
          {t('studio.feedbackList.showArchived')}
        </label>
      </div>
    </div>;
}